// deps
import React, { memo, ReactElement, useEffect, useRef, useState } from "react";

//constants
import {
    BILL_ELEMENT_ENTRIES,
    ANIMATION_DEFAULT_TIMEOUT,
    YOU_PAY,
    LOTTIE_ANIMATIONS,
    LOTTIE_ANIMATIONS_MONEY_PROPS,
    ANIMATION_TIMEOUT_MONEY,
} from "@dineout-cart-ui/constants/index";

// styles
import {
    AmountWrapper,
    BackgroundWrapper,
    ContentWrapper,
    LoyaltyOverlay,
    OriginalAmountText,
    StyledText,
    YouPay,
    StyledTextHeader,
    MoneyAnimationWrapper,
} from "./styles";

// internal deps
import { currencyFormat } from "@swiggy-private/dineout-payment-success-ui";
import { CoreLayout, LottieAnimation } from "@swiggy-private/core-ui";
import { isEmpty, getCloudinaryUrl } from "@swiggy-private/js-utils";
import { PAYMENT_CART_HEADER } from "@swiggy-private/cart-test-ids";

// components
import HeaderWrapper from "./HeaderWrapper";
import DiscountWrapper from "./DiscountWrapper";
import SavingsCallout from "./SavingsCallout";

// helpers
import { checkIfDiscountExists } from "@dineout-cart-ui/helpers/index";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";

interface IHeaderProps {
    helpIconImage?: string;
    isGIRFOrder?: string;
    mastheadBackgroundImage?: string;
    mastheadRewardsBackgroundImage?: string;
    popupSavingsBackgroundImage?: string;
    savingsBenefitsImage?: string;
    savingsImage?: string;
    savingsText?: string;
    savingsTextEMColor?: string;
    swiggyOnePlanText?: string;
    title?: string;
    widgetType?: string;
    restaurantID?: string;
    restaurantName?: string;
    restaurantLocation?: string;
    isStickHeader?: boolean;
    savingContainerExists?: boolean;
    headerTheme?: string;
    dineoutMastHeadTextWidget?: {
        affordance: boolean;
        autoScrollTime: number;
        color: string;
        text: string;
        widgetType: string;
    };
    enteredAmount: number;
    discountedAmount?: string;
    originalAmountRaw?: string;
    discountedAmountRaw?: string;
    totalBillExcludingExtras: number;
    billToPay: number;
    goBack: () => void;
    onHelpClick: () => void;
    showCouponSuccessLottie: boolean;
    resetCouponSuccessLottie: VoidFunction;
    isRewardRedeemed: boolean;
    rewardText?: string;
    savingsTextColor?: string;
    savingsTextVariant?: string;
    redeemableRewards?: number;
    tickerTextColor?: keyof IColors;
}
const PaymentCartHeader = (props: IHeaderProps): ReactElement => {
    const {
        mastheadBackgroundImage = "",
        popupSavingsBackgroundImage = "",
        helpIconImage = "",
        savingsImage = "",
        savingsText = "",
        restaurantName = "",
        restaurantLocation,
        discountedAmountRaw,
        isStickHeader,
        headerTheme,
        dineoutMastHeadTextWidget,
        goBack,
        onHelpClick,
        enteredAmount,
        title,
        savingContainerExists,
        totalBillExcludingExtras,
        showCouponSuccessLottie,
        resetCouponSuccessLottie,
        mastheadRewardsBackgroundImage,
        isRewardRedeemed,
        rewardText,
        savingsTextColor,
        savingsTextVariant,
        redeemableRewards,
        tickerTextColor,
    } = props;
    const isDiscount: boolean = checkIfDiscountExists(enteredAmount, discountedAmountRaw, savingsText);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const prevToggleRef = useRef(isRewardRedeemed);
    const [isMoneyAnimationVisible, setIsMoneyAnimationVisible] = useState(false);
    const moneyAnimationTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (prevToggleRef.current !== isRewardRedeemed) {
            setShouldAnimate(true);
            prevToggleRef.current = isRewardRedeemed;
        }
    }, [isRewardRedeemed]);

    useEffect(() => {
        if (showCouponSuccessLottie) {
            animationTimeoutRef.current = setTimeout(() => {
                resetCouponSuccessLottie();
            }, ANIMATION_DEFAULT_TIMEOUT);
        }

        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, [resetCouponSuccessLottie, showCouponSuccessLottie]);

    useEffect(() => {
        setIsMoneyAnimationVisible(false);
        if (isRewardRedeemed) {
            moneyAnimationTimerRef.current = setTimeout(() => {
                setIsMoneyAnimationVisible(true);
            }, ANIMATION_TIMEOUT_MONEY);
            return () => {
                if (moneyAnimationTimerRef.current) {
                    clearTimeout(moneyAnimationTimerRef.current);
                }
            };
        }
    }, [isRewardRedeemed]);

    return (
        <BackgroundWrapper backgroundImage={mastheadBackgroundImage} savingContainerExists={savingContainerExists}>
            {!isEmpty(mastheadRewardsBackgroundImage) && (shouldAnimate || isRewardRedeemed) && (
                <LoyaltyOverlay image={mastheadRewardsBackgroundImage} isActive={isRewardRedeemed} />
            )}

            <ContentWrapper>
                <CoreLayout.Header isBillPaymentCartHeader>
                    <HeaderWrapper
                        isStickHeader={Boolean(isStickHeader)}
                        headerTheme={headerTheme}
                        restaurantName={restaurantName}
                        restaurantLocation={restaurantLocation}
                        helpIconImage={helpIconImage}
                        goBack={goBack}
                        onHelpClick={onHelpClick}
                    ></HeaderWrapper>
                </CoreLayout.Header>

                <YouPay>
                    <StyledText
                        variant="V2.subheading1.neutral.medium"
                        color="background_Primary"
                        data-testid={PAYMENT_CART_HEADER.YOU_PAY}
                    >
                        <StyledTextHeader>{title || YOU_PAY}</StyledTextHeader>
                    </StyledText>
                    {isDiscount && ( //for strikethrough
                        <AmountWrapper>
                            <OriginalAmountText>
                                {BILL_ELEMENT_ENTRIES.INR_CURRENCY_SYMBOL} {currencyFormat(enteredAmount)}
                            </OriginalAmountText>
                        </AmountWrapper>
                    )}
                </YouPay>

                <DiscountWrapper
                    isDiscount={isDiscount}
                    showAnimation={showCouponSuccessLottie}
                    totalBillExcludingExtras={totalBillExcludingExtras}
                    enteredAmount={enteredAmount}
                    isRewardRedeemed={isRewardRedeemed}
                    shouldAnimate={shouldAnimate}
                    redeemableRewards={redeemableRewards}
                    tickerTextColor={tickerTextColor}
                ></DiscountWrapper>

                {isMoneyAnimationVisible && (
                    <MoneyAnimationWrapper>
                        <LottieAnimation
                            {...LOTTIE_ANIMATIONS_MONEY_PROPS}
                            fileName={getCloudinaryUrl(LOTTIE_ANIMATIONS.MONEY_LOTTIE)}
                            visible={true}
                            playLottie={true}
                        />
                    </MoneyAnimationWrapper>
                )}

                <SavingsCallout
                    savingsImage={savingsImage}
                    popupSavingsBackgroundImage={popupSavingsBackgroundImage}
                    savingsText={savingsText}
                    showSavingsAnimation={showCouponSuccessLottie}
                    dineoutMastHeadTextWidget={dineoutMastHeadTextWidget}
                    rewardText={rewardText}
                    savingsTextColor={savingsTextColor}
                    savingsTextVariant={savingsTextVariant}
                    isRewardRedeemed={isRewardRedeemed}
                    shouldAnimate={shouldAnimate}
                ></SavingsCallout>
            </ContentWrapper>
        </BackgroundWrapper>
    );
};

export default memo(PaymentCartHeader);
